"use client";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LogType } from "@/lib/logTypes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, RefreshCcw, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import useClientTranslation from "@/hooks/useClientTranslation";
import { TOKEN_SESSION_KEY, type TokenSessionData } from "@/lib/tokenApi";

interface LogFiltersProps {
  onSearch?: (filters: {
    startDate: string;
    endDate: string;
    tokenName: string;
    tokenGroup: string;
    logType: string;
    model: string;
    minUseTime: string;
    stream: string;
    ip: string;
    recentCount: number;
  }) => void;
  onReset?: () => void;
}

export default function LogFilters({ onSearch, onReset }: LogFiltersProps) {
  const { t } = useClientTranslation("logpg");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tokenName, setTokenName] = useState("");
  const [tokenGroup, setTokenGroup] = useState("");
  const [logType, setLogType] = useState("");
  const [model, setModel] = useState("");
  const [minUseTime, setMinUseTime] = useState("");
  const [stream, setStream] = useState("");
  const [ip, setIp] = useState("");
  const [recentCount, setRecentCount] = useState("200");
  const RECENT_COUNT_OPTIONS = ["50", "100", "200", "500", "1000", "2000"];

  // 从 sessionStorage 读取令牌下拉选项
  const [tokenNames, setTokenNames] = useState<string[]>([]);
  const [tokenGroups, setTokenGroups] = useState<string[]>([]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(TOKEN_SESSION_KEY);
      if (raw) {
        const data: TokenSessionData = JSON.parse(raw);
        setTokenNames(data.names ?? []);
        setTokenGroups(data.groups ?? []);
      }
    } catch {
      // ignore
    }
  }, []);

  // 日志类型选项 (0-6)
  const LOG_TYPE_OPTIONS = [
    { value: String(LogType.UNKNOWN), labelKey: "logTypes.unknown" },
    { value: String(LogType.RECHARGE), labelKey: "logTypes.recharge" },
    { value: String(LogType.CONSUMPTION), labelKey: "logTypes.consumption" },
    { value: String(LogType.ADMIN), labelKey: "logTypes.admin" },
    { value: String(LogType.SYSTEM), labelKey: "logTypes.system" },
    { value: String(LogType.ERROR), labelKey: "logTypes.error" },
    { value: String(LogType.REFUND), labelKey: "logTypes.refund" },
    { value: String(LogType.CALLING), labelKey: "logTypes.calling" },
  ];

  const handleSearch = () => {
    onSearch &&
      onSearch({
        startDate,
        endDate,
        tokenName,
        tokenGroup,
        logType,
        model,
        minUseTime,
        stream,
        ip,
        recentCount: Number(recentCount),
      });
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setTokenName("");
    setTokenGroup("");
    setLogType("");
    setModel("");
    setMinUseTime("");
    setStream("");
    setIp("");
    setRecentCount("200");
    onReset && onReset();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 获取开始日期之后的日期最小值（用于结束日期）
  const getMinEndDate = () => {
    if (!startDate) return "";
    // startDate 格式为 YYYY-MM-DD，直接返回即可
    return startDate;
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 第一行：时间范围、令牌名称、令牌密钥 */}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex items-end gap-2">
            <div className="grid gap-1">
              <Label className="text-xs">{t("filters.timeRange")}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-36 h-8 text-sm"
              />
            </div>
            <ArrowRight size={14} className="text-muted-foreground mb-2 shrink-0" />
            <div className="grid gap-1">
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                onKeyDown={handleKeyDown}
                min={getMinEndDate()}
                disabled={!startDate}
                className="w-36 h-8 text-sm"
              />
            </div>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">{t("filters.tokenName")}</Label>
            <Select value={tokenName} onValueChange={setTokenName}>
              <SelectTrigger size="sm" className="w-40 text-sm">
                <SelectValue placeholder={t("filters.tokenNamePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {tokenNames.map(name => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* 第二行：令牌分组、日志类型、模型、流式、IP */}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="grid gap-1">
            <Label className="text-xs">{t("filters.tokenGroup")}</Label>
            <Select value={tokenGroup} onValueChange={setTokenGroup}>
              <SelectTrigger size="sm" className="w-40 text-sm">
                <SelectValue placeholder={t("filters.tokenGroupPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {tokenGroups.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">{t("filters.logType")}</Label>
            <Select value={logType} onValueChange={setLogType}>
              <SelectTrigger size="sm" className="w-32 text-sm">
                <SelectValue placeholder={t("filters.logTypePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {LOG_TYPE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">{t("filters.model")}</Label>
            <Input
              value={model}
              onChange={e => setModel(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("filters.modelPlaceholder")}
              className="w-40 h-8 text-sm"
            />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">{t("filters.minUseTime")}</Label>
            <Input
              type="number"
              value={minUseTime}
              onChange={e => setMinUseTime(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("filters.minUseTimePlaceholder")}
              className="w-36 h-8 text-sm"
              min={0}
            />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">{t("filters.stream")}</Label>
            <Select value={stream} onValueChange={setStream}>
              <SelectTrigger size="sm" className="w-32 text-sm">
                <SelectValue placeholder={t("filters.streamPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">{t("yes")}</SelectItem>
                <SelectItem value="0">{t("no")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">{t("filters.ip")}</Label>
            <Input
              value={ip}
              onChange={e => setIp(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("filters.ipPlaceholder")}
              className="w-36 h-8 text-sm"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Label className="text-xs shrink-0">{t("filters.recentCount") ?? "日志条数"}</Label>
            <RadioGroup
              value={recentCount}
              onValueChange={setRecentCount}
              className="flex items-center gap-2.5 flex-wrap"
            >
              {RECENT_COUNT_OPTIONS.map((val) => (
                <div key={val} className="flex items-center gap-1">
                  <RadioGroupItem value={val} id={`recent-${val}`} />
                  <Label htmlFor={`recent-${val}`} className="cursor-pointer text-xs font-normal">
                    {val}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RefreshCcw size={14} /> {t("filters.reset")}
            </Button>
            <Button size="sm" onClick={handleSearch}>
              <Search size={14} /> {t("filters.search")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
